/**
 * Одна цена вилки — одно проставление.
 *
 * На проводе у вилки два места с кэфом: верхнее `o` и `legs[].odds`, плюс у
 * биржи ещё `share` и стакан. Дельта часто привозит только `o` (shallow merge
 * оставляет старые ноги), и тогда экран показывает новый кэф, а шеры, сумму и
 * купон — вчерашние. Человек ставит по мёртвому тикету.
 *
 * Закон: уехавший кэф увозит всё, чем ставят. Сверяем ноги с `o` до расчёта
 * строки, не после клика.
 */

import { isPreparedFeed2Id } from './feedContext';
import type { Fork, ForkLeg } from './protocol';

/** Шаг цены шара, с которым сверяем «то же число»: десятая доля цента. */
const TICK = 0.001;

/** Цена шара из кэфа. Четыре знака вверх — как `_share_price` на сервере. */
export function shareFromOdds(odds: string): string | null {
  const n = Number.parseFloat(odds);
  if (!Number.isFinite(n) || n <= 1) return null;
  return String(Math.ceil(1 / n * 10_000 - 1e-12) / 10_000);
}

const implied = (odds: string): number | null => {
  const n = Number.parseFloat(odds);
  return Number.isFinite(n) && n > 1 ? 1 / n : null;
};

const off = (price: number, want: number): boolean => Math.abs(price - want) > TICK;

function alignLeg(leg: ForkLeg, headline: string | undefined, keepBook = false): ForkLeg {
  const odds = headline ?? leg.odds;
  let out = odds === leg.odds ? leg : { ...leg, odds };

  const want = implied(out.odds);
  if (want === null) return out;

  if (out.book !== undefined && out.book.length > 0) {
    const best = Number.parseFloat(out.book[0]![0]);
    if (!Number.isFinite(best) || off(best, want)) {
      if (keepBook) return out;
      const { book: _stale, ...rest } = out;
      return { ...rest, share: shareFromOdds(out.odds) ?? rest.share };
    }
    return out;
  }

  if (out.unit !== 'share' && (out.share == null || out.share === '')) return out;
  const named = Number.parseFloat(out.share ?? '');
  // Шар вне (0, 1) — поломка поля, не вчерашняя цена. Замазывать кэфом нельзя:
  // sharePrice вернёт ногу к тому, что про неё точно известно.
  if (Number.isFinite(named) && (named <= 0 || named >= 1)) return out;
  if (Number.isFinite(named) && !off(named, want)) return out;
  const share = shareFromOdds(out.odds);
  return share === null || share === out.share ? out : { ...out, share };
}

/**
 * Ноги смотрят туда же, куда `o`. Ссылка та же, если уже сходилось — кэш
 * строк сравнивает вилку по ссылке и не должен промахиваться зря.
 */
export function alignFork(fork: Fork): Fork {
  const legs = fork.legs;
  if (legs === undefined || legs.length === 0) return fork;
  const keepBook = isPreparedFeed2Id(fork.id);
  const next = legs.map((leg, i) => alignLeg(leg, fork.o?.[i], keepBook));
  return next.some((leg, i) => leg !== legs[i]) ? { ...fork, legs: next } : fork;
}

/**
 * Отпечаток того, чем ставят: рынок, линия, кэфы, шеры, адрес. Сменился —
 * вчерашний купон больше не открывают.
 */
export function placeMark(fork: Fork, bank: number, fx: number): string {
  const one = alignFork(fork);
  return [
    one.id,
    one.market ?? '',
    one.line ?? '',
    ...(one.legs ?? []).map((leg) =>
      [leg.s, leg.odds, leg.share ?? '', leg.side, leg.name ?? '', leg.betUrl ?? '', leg.url ?? ''].join(
        '\x1f',
      ),
    ),
    String(bank),
    String(fx),
  ].join('|');
}
